import { styled } from '@mui/material/styles';
import { CircularProgress, IconButton, InputLabel } from '@mui/material';
import { QButton, QContainer, QThemeProvider, QTypography } from '@kw/quokka-ui';
import { ChangeEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import BackButton from '../../shared/BackButton';
import { Link } from '../../../Link';
import { httpUploadRequest } from '../../../utils/network.service';
import { appIconDataUrl } from '../../../utils/appIconDataUrl';
import { displayFileSize, validateBinaryFile } from './binaryUploadValidation';
import { APPLICATIONS_ALL_STATUSES_PATH } from './requestAnalysisNavigation';

dayjs.extend(utc);

/** 201 body from POST /application/orgapp/binary-submit (device-service BinarySubmitResponse). */
export interface BinarySubmitSuccess {
  applicationId: string;
  name?: string;
  os: 'android' | 'ios';
  version: string;
  manifestVersion?: string;
  sha256: string;
  mastId?: string;
  iconBase64?: string;
  reused: boolean;
}

/** `duplicate` payload embedded in the 409 body (device-service BinaryDuplicateSubmission). */
export interface BinaryDuplicateSubmission {
  name?: string;
  applicationId: string;
  os: 'android' | 'ios';
  version: string;
  manifestVersion?: string;
  sha256: string;
  iconBase64?: string;
  submittedAt?: string;
  analysisStatus?: string;
  /** eval's analysis id, present only once the prior analysis is COMPLETE. */
  analysisId?: string;
}

const GENERIC_UPLOAD_ERROR = 'Something went wrong while uploading your file. Please try again.';

const PlatformBadge = ({ os }: { os: 'android' | 'ios' }) => (
  <StyledPlatformBadge>
    <img width={os === 'ios' ? 12 : 16} height={16} src={os === 'ios' ? '/icons/apple.png' : '/icons/android.png'} alt={os} />
    <QTypography variant="body2">{os === 'ios' ? 'iOS' : 'Android'}</QTypography>
  </StyledPlatformBadge>
);

const SubmittedAppCard = ({ app }: { app: BinarySubmitSuccess | BinaryDuplicateSubmission }) => (
  <StyledAppCard>
    <StyledAppCardHeader>
      {app.iconBase64 ? (
        <StyledAppIcon src={appIconDataUrl(app.iconBase64)} alt="app icon" />
      ) : (
        <StyledAppIcon src="/icons/app-placeholder-60x60.svg" alt="app icon placeholder" />
      )}
      <QTypography variant="h5Bold">{app.name || app.applicationId}</QTypography>
      <PlatformBadge os={app.os} />
    </StyledAppCardHeader>
    <StyledAppCardRows>
      <StyledAppCardRow>
        <QTypography variant="bodyRegular">Version</QTypography>
        <QTypography variant="bodyBold">{app.manifestVersion || app.version}</QTypography>
      </StyledAppCardRow>
      <StyledAppCardRow>
        <QTypography variant="bodyRegular">Package Name</QTypography>
        <QTypography variant="bodyBold">{app.applicationId}</QTypography>
      </StyledAppCardRow>
      <StyledAppCardRow>
        <QTypography variant="bodyRegular">SHA256</QTypography>
        <QTypography variant="bodyBold">{app.sha256}</QTypography>
      </StyledAppCardRow>
    </StyledAppCardRows>
  </StyledAppCard>
);

const ResultActions = () => {
  const navigate = useNavigate();
  return (
    <StyledResultActions>
      <QButton variant="outlined" color="primary" onClick={() => navigate('/request-analysis')}>
        Return to Request Analysis
      </QButton>
      <QButton variant="contained" color="primary" onClick={() => navigate(APPLICATIONS_ALL_STATUSES_PATH)}>
        Go to Applications List
      </QButton>
    </StyledResultActions>
  );
};

const ResultHeader = ({ icon, title, completedAt }: { icon: JSX.Element; title: string; completedAt: string }) => (
  <StyledResultHeader>
    <StyledResultTitle>
      {icon}
      <QTypography variant="h4Bold">{title}</QTypography>
    </StyledResultTitle>
    <QTypography variant="body2">{dayjs.utc(completedAt).format('MMM D, YYYY · h:mm A [UTC]')}</QTypography>
  </StyledResultHeader>
);

const RequestCompleteScreen = ({ result, completedAt }: { result: BinarySubmitSuccess; completedAt: string }) => (
  <StyledResultWrapper>
    <ResultHeader
      icon={<CheckCircleOutlineIcon sx={{ color: '#34bfa3', fontSize: '28px' }} />}
      title="Request Complete"
      completedAt={completedAt}
    />
    <SubmittedAppCard app={result} />
    <StyledNote role="note">
      <StyledNoteLabel>Note:</StyledNoteLabel> It may take a few minutes for this submission to appear in the Applications page.
    </StyledNote>
    <ResultActions />
  </StyledResultWrapper>
);

const DuplicateSubmissionScreen = ({ duplicate, completedAt }: { duplicate: BinaryDuplicateSubmission; completedAt: string }) => {
  const submittedAtText = duplicate.submittedAt ? dayjs.utc(duplicate.submittedAt).format('MMM D, YYYY [at] h:mm A [UTC]') : '';
  const analysisComplete = duplicate.analysisStatus === 'COMPLETE';
  // Deep-link the finished report. device-service resolves the id from eval, but leaves it off when
  // eval is unreachable — fall back to locating the app in the list rather than dropping the link.
  const analysisHref = duplicate.analysisId
    ? `/applications/app-detail-report?analysisId=${encodeURIComponent(duplicate.analysisId)}`
    : `/applications?searchQuery=${encodeURIComponent(duplicate.name || duplicate.applicationId)}`;
  return (
    <StyledResultWrapper>
      <ResultHeader
        icon={<ErrorOutlineIcon sx={{ color: '#eab308', fontSize: '28px' }} />}
        title="Duplicate Submission"
        completedAt={completedAt}
      />
      <SubmittedAppCard app={duplicate} />
      <StyledNote tone="warning" role="note">
        {analysisComplete ? (
          <>
            <StyledNoteLabel>Note:</StyledNoteLabel> This file was previously submitted{submittedAtText && ` on ${submittedAtText}`}. You
            can view its completed analysis <StyledNoteLink href={analysisHref}>here</StyledNoteLink>.
          </>
        ) : (
          <>
            <StyledNoteLabel>Note:</StyledNoteLabel> This file was previously submitted{submittedAtText && ` on ${submittedAtText}`} and its
            analysis is currently in progress. Please check the Applications page and filter by Status to monitor progress.
          </>
        )}
      </StyledNote>
      <ResultActions />
    </StyledResultWrapper>
  );
};

export default function RequestAnalysisUploadPackage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Client-side validation failure for the current selection — blocks the Upload button.
  const [warningMessage, setWarningMessage] = useState<string>('');
  // Server/network failure from an attempted upload — shown inline but must NOT block a retry of the
  // same (valid) file, so it is kept separate from the validation warning above.
  const [uploadError, setUploadError] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const [result, setResult] = useState<BinarySubmitSuccess | null>(null);
  const [duplicate, setDuplicate] = useState<BinaryDuplicateSubmission | null>(null);
  const [completedAt, setCompletedAt] = useState<string>('');

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError('');
      // 1st check — validate the selection right away
      setWarningMessage(validateBinaryFile(file));
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setWarningMessage('');
    setUploadError('');
  };

  const handleUpload = async () => {
    if (!selectedFile || warningMessage || uploading) return;
    setUploadError('');
    setUploading(true);

    const { data, response, error } = await httpUploadRequest({
      path: `${process.env.DEVICE_SERVICE_URL}/application/orgapp/binary-submit`,
      file: selectedFile,
    });

    setUploading(false);

    if (error || !response) {
      setUploadError(GENERIC_UPLOAD_ERROR);
      return;
    }

    if (response.status === 201) {
      setCompletedAt(new Date().toISOString());
      setResult(data as BinarySubmitSuccess);
      return;
    }

    if (response.status === 409 && data?.duplicate) {
      setCompletedAt(new Date().toISOString());
      setDuplicate(data.duplicate as BinaryDuplicateSubmission);
      return;
    }

    if (response.status === 413) {
      // server-side size limit — mirror the client-side message
      setUploadError('Upload a file less than 200 MB in size');
      return;
    }

    // 2nd check — anything else (e.g. 422 unparseable binary) surfaces the server's message inline
    setUploadError(typeof data?.message === 'string' ? data.message : GENERIC_UPLOAD_ERROR);
  };

  const renderUploadForm = () => (
    <StyledUploadWrapper>
      <QTypography variant="h4Bold" sx={{ marginTop: '32px', marginBottom: '32px' }}>
        Upload a Package
      </QTypography>
      {selectedFile ? (
        <>
          <StyledSelectedFile error={!!warningMessage || !!uploadError}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: '24px' }} />
            <QTypography variant="bodyBold">{selectedFile.name}</QTypography>
            <QTypography variant="body1" sx={{ color: '#757575', fontSize: '14px', lineHeight: '21px', fontWeight: '400' }}>
              {displayFileSize(selectedFile.size)}
            </QTypography>
            <IconButton aria-label="delete" onClick={handleRemoveFile} sx={{ color: '#e15241' }}>
              <DeleteOutlinedIcon />
            </IconButton>
          </StyledSelectedFile>
          <StyledWarningMessage>
            {warningMessage || uploadError ? (
              <>
                <ErrorOutlineIcon />
                <div>{warningMessage || uploadError}</div>
              </>
            ) : null}
          </StyledWarningMessage>
          <QButton
            variant="filled"
            color="primary"
            sx={{ marginTop: '16px', minWidth: '120px', height: '40px' }}
            onClick={handleUpload}
            disabled={!!warningMessage || uploading}
          >
            <CircularProgress size={24} sx={{ color: 'white', display: uploading ? 'inline-block' : 'none' }} />
            {uploading ? '' : 'Upload'}
          </QButton>
        </>
      ) : (
        <>
          <FileUploadOutlinedIcon sx={{ fontSize: '56px', color: '#3f51b5' }} />
          <QTypography variant="body1" sx={{ marginTop: '16px' }}>
            Upload an APK, AAB or IPA file
          </QTypography>
          <QTypography variant="body1" sx={{ marginTop: '4px', color: '#666' }}>
            Maximum size: 200MB
          </QTypography>
          <StyledFileUploadLabel htmlFor="binary-package-upload" sx={{ marginTop: '16px' }}>
            <QButton variant="filled-no-hover" color="primary" sx={{ height: '40px' }} component="div">
              Choose File
            </QButton>
            <input type="file" accept=".apk,.aab,.ipa" id="binary-package-upload" onChange={handleFileChange} />
          </StyledFileUploadLabel>
        </>
      )}
    </StyledUploadWrapper>
  );

  const renderContent = () => {
    if (result) return <RequestCompleteScreen result={result} completedAt={completedAt} />;
    if (duplicate) return <DuplicateSubmissionScreen duplicate={duplicate} completedAt={completedAt} />;
    return renderUploadForm();
  };

  return (
    <QThemeProvider>
      <QContainer
        sx={{
          paddingTop: '16px',
          paddingBottom: '0px',
          maxWidth: '100% !important',
          display: 'flex',
          flexDirection: 'column',
          rowGap: 2,
          overflow: 'auto',
        }}
      >
        <BackButton href="/request-analysis" text="Back to Request Analysis" />
        {renderContent()}
      </QContainer>
    </QThemeProvider>
  );
}

const StyledUploadWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledFileUploadLabel = styled(InputLabel)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  input {
    display: none;
  }
`;

const StyledSelectedFile = styled('div', { shouldForwardProp: prop => prop !== 'error' })<{ error?: boolean }>(({ error }) => ({
  borderRadius: '4px',
  background: '#f8f8f8',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '8px',
  width: '700px',
  maxWidth: '100%',
  height: '56px',
  padding: '12px 16px',
  border: error ? '1px solid #e15241' : '1px solid #bdbdbd',
  button: {
    marginLeft: 'auto',
    marginRight: '0',
  },
}));

const StyledWarningMessage = styled(QTypography)`
  color: #da1e28;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  margin: 16px 0;
  min-height: 25px;
  display: flex;
  width: 700px;
  max-width: 100%;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const StyledResultWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  max-width: 1100px;
  width: 100%;
  margin: 24px auto 0;
`;

const StyledResultHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledResultTitle = styled('div')`
  display: flex;
  align-items: center;
  column-gap: 10px;
`;

const StyledAppCard = styled('div')(({ theme }) => ({
  border: '1px solid #bdbdbd',
  borderRadius: '8px',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(2),
}));

const StyledAppCardHeader = styled('div')`
  display: flex;
  align-items: center;
  column-gap: 12px;
`;

const StyledAppIcon = styled('img')`
  width: 40px;
  height: 40px;
  border-radius: 8px;
`;

const StyledPlatformBadge = styled('div')`
  display: flex;
  align-items: center;
  column-gap: 6px;
`;

const StyledAppCardRows = styled('div')`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

const StyledAppCardRow = styled('div')`
  display: grid;
  grid-template-columns: 160px 1fr;
  column-gap: 16px;
  word-break: break-all;
`;

const StyledResultActions = styled('div')`
  display: flex;
  justify-content: center;
  column-gap: 24px;
  /* the actions sit 32px below the note — 16px of that comes from the wrapper's row-gap */
  margin-top: 16px;
`;

/*
 * `info` mirrors the note on the store-app confirmation screen (quokka-ui's
 * AnalysisRequestConfirmation), which does not export its StyledNote; `warning` is the same note in
 * the amber palette used for duplicate submissions. Kept in sync by hand — if quokka-ui ever exports
 * its note, or gains a QNote, switch to that instead of these copies.
 */
const NOTE_TONES = {
  info: { background: '#f5f7ff', border: '#d5ddf8', label: '#356bd4' },
  // #ffb822 is the theme's state.warning; the background is that same colour at 10%
  warning: { background: 'rgba(255, 184, 34, 0.1)', border: '#ffb822', label: '#8c5111' },
};

type NoteTone = keyof typeof NOTE_TONES;

const StyledNoteLabel = styled('span')`
  font-weight: 800;
`;

const StyledNote = styled('div', { shouldForwardProp: prop => prop !== 'tone' })<{ tone?: NoteTone }>`
  padding: 8px 16px;
  border: 1px solid ${({ tone = 'info' }) => NOTE_TONES[tone].border};
  border-radius: 8px;
  background: ${({ tone = 'info' }) => NOTE_TONES[tone].background};
  color: #212121;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;

  ${StyledNoteLabel} {
    color: ${({ tone = 'info' }) => NOTE_TONES[tone].label};
  }
`;

/* reads as part of the sentence rather than carrying MUI's teal link colour */
const StyledNoteLink = styled(Link)`
  color: inherit;
  text-decoration: underline;
`;
